/**
 * Pure utility functions for classifying RTK Query mutation endpoints
 * into activity log action types, categories, and human-readable descriptions.
 */

export type ActionType = "create" | "update" | "delete" | "form_submit" | "other";
export type Category = "data" | "interaction" | "auth";

/**
 * Endpoints that should NOT be tracked here: high-frequency or internal ones, and
 * access changes, which the server records itself with what changed before and
 * after (a row from here would only duplicate it without the detail).
 */
export const SKIPPED_ENDPOINTS: ReadonlySet<string> = new Set([
  "sendSkyAgentMessage",
  "parseResume",
  "regenerateAiSummary",
  "submitPublicSurveyResponse",
  "saveCostTracking",
  // Access changes, audited server-side.
  "createLocationGroup",
  "updateLocationGroup",
  "deleteLocationGroup",
  "createDepartment",
  "renameDepartment",
  "setDepartmentActive",
  "saveJobFixes",
  "saveUserScope",
]);

const CLASSIFICATION_RULES: Array<[RegExp, ActionType]> = [
  [/^(create|add|duplicate|clone|seed|generate|regenerate|upload)/, "create"],
  [/^delete/, "delete"],
  [/^(send|submit|resend)/, "form_submit"],
  [
    /^(update|upsert|reorder|toggle|set|change|reset|publish|unpublish|block|unblock|assign|remove|disconnect|cancel|approve|reject|save|revoke|sync|test)/,
    "update",
  ],
];

export function classifyEndpoint(endpointName: string): ActionType {
  for (const [pattern, actionType] of CLASSIFICATION_RULES) {
    if (pattern.test(endpointName)) return actionType;
  }
  return "other";
}

export function classifyCategory(actionType: ActionType): Category {
  switch (actionType) {
    case "create":
    case "update":
    case "delete":
      return "data";
    case "form_submit":
      return "interaction";
    default:
      return "interaction";
  }
}

const IRREGULARS: Record<string, string> = {
  send: "Sent",
  submit: "Submitted",
  set: "Set",
  delete: "Deleted",
  create: "Created",
  update: "Updated",
  add: "Added",
  remove: "Removed",
  duplicate: "Duplicated",
  cancel: "Cancelled",
  resend: "Resent",
  publish: "Published",
  unpublish: "Unpublished",
  block: "Blocked",
  unblock: "Unblocked",
  assign: "Assigned",
  upload: "Uploaded",
  generate: "Generated",
  regenerate: "Regenerated",
  seed: "Seeded",
  save: "Saved",
  reorder: "Reordered",
  toggle: "Toggled",
  change: "Changed",
  reset: "Reset",
  clone: "Cloned",
  disconnect: "Disconnected",
  upsert: "Updated",
  approve: "Approved",
  reject: "Rejected",
  revoke: "Revoked",
  sync: "Synced",
  test: "Tested",
};

function pastTense(verb: string): string {
  const lower = verb.toLowerCase();
  if (IRREGULARS[lower]) return IRREGULARS[lower];
  if (lower.endsWith("e")) return lower + "d";
  return lower + "ed";
}

const DESCRIPTION_OVERRIDES: Record<string, string> = {
  bulkUpdateStatus: "Bulk updated applicant statuses",
  bulkUpdateTags: "Bulk updated applicant tags",
  updateApplicantStatus: "Updated applicant status",
  updateApplicantRating: "Updated applicant rating",
  updateApplicantTags: "Updated applicant tags",
  updateApplicantProfile: "Updated applicant profile",
  assignJobToPortals: "Assigned job to portals",
  removeJobFromPortal: "Removed job from portal",
  setDefaultPortal: "Set default portal",
  setDefaultTemplate: "Set default survey template",
  sendSurveyRequests: "Sent survey requests",
  resendSurveyRequest: "Resent survey request",
  changeSurveyStatus: "Changed survey status",
  blockUser: "Blocked user",
  unblockUser: "Unblocked user",
  updateRoleConfig: "Updated role configuration",
  resetRoleConfig: "Reset role configuration",
  upsertEmailSignature: "Updated email signature",
  generatePortalApiKey: "Generated portal API key",
  revokePortalApiKey: "Revoked portal API key",
  uploadPortalImage: "Uploaded portal image",
  seedPipelineStages: "Seeded pipeline stages",
  togglePipelineStage: "Toggled pipeline stage",
  syncLeadToCrm: "Synced lead to CRM",
  testCrmIntegration: "Tested CRM integration",
  assignCandidateToInterviewer: "Assigned candidate to interviewer",
  removeInterviewerAssignment: "Removed interviewer assignment",
  disconnectMicrosoft: "Disconnected Microsoft account",
  /*
   * Integrations, spelled out. The generic humanizer turned these into
   * "Created indeed integration" and "Deleted linkedin integration", which
   * is both lower-cased mid-sentence and the wrong verb: what the user did
   * was connect or disconnect a third party that then starts or stops
   * receiving job postings. These read as that on the page's own activity
   * list.
   */
  createIndeedIntegration: "Connected Indeed",
  updateIndeedIntegration: "Updated the Indeed connection",
  deleteIndeedIntegration: "Disconnected Indeed",
  testIndeedIntegration: "Tested the Indeed feed",
  createLinkedInIntegration: "Connected LinkedIn",
  updateLinkedInIntegration: "Updated the LinkedIn connection",
  deleteLinkedInIntegration: "Disconnected LinkedIn",
  testLinkedInIntegration: "Tested the LinkedIn feed",
  approvePendingAction: "Approved SkyAgent action",
  rejectPendingAction: "Rejected SkyAgent action",
  updateInstanceIndustry: "Updated instance industry",
  updateTourStatus: "Updated interview status",
  cancelTour: "Cancelled interview",
  resendLeadEmail: "Resent lead email",
  addLeadNote: "Added lead note",
  addApplicantNote: "Added applicant note",
  updateJobPortalAssignment: "Updated job portal assignment",
};

export function humanizeEndpoint(endpointName: string): string {
  if (DESCRIPTION_OVERRIDES[endpointName]) {
    return DESCRIPTION_OVERRIDES[endpointName];
  }

  // Split camelCase into words
  const words = endpointName
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ");
  if (words.length === 0) return endpointName;

  // Past-tense the first word (the verb)
  words[0] = pastTense(words[0]);

  return words.map((w, i) => (i === 0 ? w : w.toLowerCase())).join(" ");
}
